// src/components/pwa/steps/common/MyAccountScreen.js
// Dedicated My Account page with Account Details, Transaction History, Statements, Wallet Permissions.

import React from 'react';
import { useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { ChevronDown, Download, FileText, Search, Share2 } from 'react-feather';
import { MANAGER_ACCOUNTS } from './accountData';
import PageTemplate from '../common/templates/PageTemplate';
import FedwireInstructionsModal from './FedwireInstructionsModal';
import '../../../styles/AccountManager.css';

// ─────────────────────────────────────────────
// ACCOUNT SELECTOR (same design as AccountManagerLayout)
// ─────────────────────────────────────────────

const SelectorContainer = styled.div`
    background-color: ${p => p.theme.colors.background.paper};
    padding: 15px;
    border-radius: 10px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const SelectorLabel = styled.p`
    margin-bottom: 10px;
    font-size: 14px;
    color: ${p => p.theme.colors.text.secondary};
    margin-top: 0;
`;

const DropdownWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const StyledSelect = styled.select`
    background: ${p => p.theme.colors.border.main};
    border: none;
    font-weight: 600;
    color: ${p => p.theme.colors.text.primary};
    padding: 10px 35px 10px 15px;
    border-radius: 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 15px;
    width: 100%;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    outline: none;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    &:hover { background-color: ${p => p.theme.colors.border.hover}; }
`;

const DropdownIcon = styled(ChevronDown)`
    position: absolute;
    right: 12px;
    pointer-events: none;
    color: ${p => p.theme.colors.text.primary};
`;

// ─────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────

const MOCK_TRANSACTIONS = [
  { date: '02/20/2026', desc: 'ACH - Vendor X', amount: '-$15,000.00', status: 'Cleared', isDebit: true },
  { date: '02/19/2026', desc: 'Wire - Tech Corp', amount: '+$250,000.00', status: 'Received', isDebit: false },
  { date: '02/18/2026', desc: 'RTP & FedNow Payment', amount: '+$5,000.00', status: 'Processed', isDebit: false },
  { date: '02/17/2026', desc: 'ACH - Vendor X', amount: '-$15,000.00', status: 'Cleared', isDebit: true },
  { date: '02/16/2026', desc: 'RTP & FedNow - Client', amount: '+$5,000.00', status: 'Processed', isDebit: false },
  { date: '02/15/2026', desc: 'ACH Payment', amount: '-$15,000.00', status: 'Cleared', isDebit: true },
  { date: '02/14/2026', desc: 'Wire - Tech Corp', amount: '+$250,000.00', status: 'Received', isDebit: false },
  { date: '02/13/2026', desc: 'RTP & FedNow - Client', amount: '+$5,000.00', status: 'Processed', isDebit: false },
];

const STMT_MONTHS = [
  'February 2026', 'January 2026', 'December 2025', 'November 2025',
  'October 2025', 'September 2025', 'August 2025', 'July 2025',
  'June 2025', 'May 2025', 'April 2025', 'March 2025',
];

// ─────────────────────────────────────────────
// STYLED COMPONENTS (content panels)
// ─────────────────────────────────────────────

const ContentPanel = styled.div`
    background: ${p => p.theme.colors.text.contrast};
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    margin-top: 0px;
`;

/* Transaction History */
const TxToolbar = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;gap:12px;flex-wrap:wrap;`;
const TxSearchBox = styled.div`
  display:flex;align-items:center;gap:7px;background:${p => p.theme.colors.background.main};border:1px solid ${p => p.theme.colors.background.selected};border-radius:9px;padding:6px 11px;flex:1;max-width:300px;
  input{border:none;background:none;outline:none;font-size:13px;color:${p => p.theme.colors.text.primary};width:100%;&::placeholder{color:${p => p.theme.colors.text.disabled};}}
`;
const ExportGroup = styled.div`display:flex;align-items:center;gap:7px;`;
const ExportLabel = styled.span`font-size:11px;font-weight:600;color:${p => p.theme.colors.text.disabled};text-transform:uppercase;letter-spacing:0.5px;`;
const ExportBtn = styled.button`
  display:flex;align-items:center;gap:5px;padding:6px 13px;border-radius:8px;font-size:12.5px;font-weight:600;cursor:pointer;transition:all 0.15s;
  border:1.5px solid ${p => p.theme.colors.text.dark};background:${p => p.theme.colors.background.paper};color:${p => p.theme.colors.text.dark};
  &:hover{background:${p => p.theme.colors.background.selected};}
`;
const PaginationContainer = styled.div`
  display: flex; justify-content: center; align-items: center; padding-top: 16px; margin-top: 8px; border-top: 1px solid ${p => p.theme.colors.background.selected};
`;
const PageControls = styled.div`display: flex; gap: 6px; align-items: center;`;
const PageBtn = styled.button`
  padding: 6px 12px; 
  border-radius: 8px; 
  font-size: 13px; 
  font-weight: 600; 
  border: 1px solid ${p => p.$active ? p.theme.colors.primary.main : p.theme.colors.border.main}; 
  background: ${p => p.$active ? p.theme.colors.primary.main : p.theme.colors.background.paper}; 
  color: ${p => p.$active ? p.theme.colors.text.contrast : p.theme.colors.text.secondary}; 
  cursor: pointer; 
  transition: all 0.2s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):hover { 
    background: ${p => p.$active ? p.theme.colors.primary.main : p.theme.colors.background.main}; 
    border-color: ${p => p.$active ? p.theme.colors.primary.main : p.theme.colors.border.hover}; 
  }
`;
const TxHead = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr 120px;
  gap: 8px;
  padding: 7px 10px;
  background: ${p => p.theme.colors.background.main};
  border-radius: 8px;
  margin-bottom: 4px;
  @media (max-width: 768px) { display: none; }
`;
const TxHeadCell = styled.span`font-size:10.5px;font-weight:700;color:${p => p.theme.colors.text.disabled};text-transform:uppercase;letter-spacing:0.5px;`;
const TxRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr 120px;
  gap: 8px;
  padding: 11px 10px;
  border-bottom: 1px solid ${p => p.theme.colors.background.main};
  align-items: center;
  transition: background 0.15s;
  border-radius: 7px;
  &:hover { background: ${p => p.theme.colors.background.main}; }
  &:last-child { border-bottom: none; }
  @media (max-width: 768px) {
    grid-template-columns: 75px 1fr 100px;
    gap: 6px;
    padding: 10px 6px;
  }
`;
const TxCell = styled.span`
  font-size: 13px;
  color: ${p => p.color || p.theme.colors.primary.light};
  font-weight: ${p => p.bold ? '600' : '400'};
  @media (max-width: 768px) { font-size: 12px; }
`;

/* Statements */
const StmtRow = styled.div`display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin-bottom:20px;`;
const StmtDownloadBtn = styled.button`display:flex;align-items:center;gap:7px;padding:9px 20px;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;background:${p => p.theme.colors.text.primary};color:${p => p.theme.colors.text.contrast};border:none;transition:opacity 0.18s;&:hover{opacity:0.85;}`;
const StmtList = styled.div`display:flex;flex-direction:column;gap:8px;`;
const StmtItem = styled.div`display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:${p => p.theme.colors.background.main};border-radius:12px;border:1px solid ${p => p.theme.colors.background.selected};gap:12px;`;
const StmtItemTitle = styled.p`font-size:13px;font-weight:600;color:${p => p.theme.colors.text.primary};margin:0;`;
const StmtItemSub = styled.p`font-size:11px;color:${p => p.theme.colors.text.disabled};margin:2px 0 0;`;
const StmtDlBtn = styled.button`display:flex;align-items:center;gap:5px;padding:6px 14px;border-radius:8px;font-size:12px;font-weight:600;background:none;border:1.5px solid ${p => p.theme.colors.border.main};color:${p => p.theme.colors.primary.light};cursor:pointer;transition:all 0.15s;&:hover{background:${p => p.theme.colors.background.selected};border-color:${p => p.theme.colors.border.hover};}`;

const StmtSelect = styled(StyledSelect)`
  width: auto;
  min-width: 180px;
`;

/* Account Details */
const DetailCard = styled.div`background:${p => p.theme.colors.background.main};border:1px solid ${p => p.theme.colors.background.selected};border-radius:12px;padding:16px 18px;`;
const DetailLabel = styled.p`font-size:11px;font-weight:700;color:${p => p.theme.colors.text.disabled};text-transform:uppercase;letter-spacing:0.5px;margin:0 0 6px;`;
const DetailValue = styled.p`font-size:15px;font-weight:700;color:${p => p.theme.colors.text.primary};margin:0;word-break:break-all;`;

const AccountDetailsTab = ({ account, onShowInstructions }) => {
  return (
    <ContentPanel style={{ border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.03)', padding: '24px 20px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', width: '100%' }}>
          <div style={{ flex: '1', minWidth: '240px' }}>
            <DetailCard style={{ border: 'none', background: 'var(--background-main, rgba(0,0,0,0.015))', padding: '16px 18px' }}>
              <DetailLabel>Account Number</DetailLabel>
              <DetailValue style={{ fontFamily: 'monospace', fontSize: '16px', letterSpacing: '0.2px' }}>{account.accountNumber}</DetailValue>
            </DetailCard>
          </div>
          <div style={{ flex: '1', minWidth: '240px' }}>
            <DetailCard style={{ border: 'none', background: 'var(--background-main, rgba(0,0,0,0.015))', padding: '16px 18px' }}>
              <DetailLabel>Total Balance ({account.currency})</DetailLabel>
              <DetailValue style={{ fontSize: '24px', fontWeight: '800', letterSpacing: '-0.5px' }}>
                {account.currency} {account.totalBalance}
              </DetailValue>
            </DetailCard>
          </div>
        </div>

        <button 
          onClick={onShowInstructions}
          style={{
            marginTop: '8px',
            width: '100%',
            padding: '14px 24px',
            background: '#111827',
            color: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '13.5px',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(17, 24, 39, 0.15)',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#1f2937';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = '#111827';
            e.currentTarget.style.transform = 'none';
          }}
        >
          <Share2 size={14} style={{ marginRight: '4px' }} />
          View Wire &amp; ACH Instructions
        </button>
      </div>
    </ContentPanel>
  );
};

const TransactionHistoryTab = () => {
  const theme = useTheme();
  const [search, setSearch] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 15;

  const filtered = MOCK_TRANSACTIONS
    .filter(tx => !search || tx.desc.toLowerCase().includes(search.toLowerCase()));

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginatedData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleExcel = () => {
    // Build an HTML table Excel can open natively
    const rows = MOCK_TRANSACTIONS.map(t =>
      `<tr><td>${t.date}</td><td>${t.desc}</td><td>${t.type}</td><td>${t.amount}</td><td>${t.status}</td></tr>`
    ).join('');
    const html = `<html><head><meta charset="utf-8"><style>table{border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px 10px;font-size:12px}th{background:${theme.colors.background.selected};font-weight:700}</style></head><body><table><tr><th>Date</th><th>Description</th><th>Type</th><th>Amount</th><th>Status</th></tr>${rows}</table></body></html>`;
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([html], { type: 'application/vnd.ms-excel' })),
      download: 'nybanq_transactions.xls',
    });
    a.click();
  };

  const handlePDF = () => {
    const rows = MOCK_TRANSACTIONS.map(t =>
      `<tr><td>${t.date}</td><td>${t.desc}</td><td>${t.type}</td><td style="color:${t.isDebit ? '#dc2626' : theme.colors.secondary.main}">${t.amount}</td><td>${t.status}</td></tr>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>NYBANQ Transactions</title><style>body{font-family:sans-serif;font-size:12px;padding:20px}h2{margin-bottom:16px}table{width:100%;border-collapse:collapse}th,td{border:1px solid ${theme.colors.border.main};padding:7px 10px;text-align:left}th{background:${theme.colors.background.main};font-weight:700;font-size:11px;text-transform:uppercase}@media print{button{display:none}}</style></head><body><h2>NYBANQ — Transaction History</h2><table><tr><th>Date</th><th>Description</th><th>Type</th><th>Amount</th><th>Status</th></tr>${rows}</table></body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  // Helper function to render pagination numbers
  const renderPaginationNumbers = () => {
    const pageNumbers = [];

    // Add first page
    pageNumbers.push(1);

    // logic to add ellipses and middle pages in a complex pagination component if needed
    // for just 5 items it's fine. We use a simple render here.
    if (totalPages > 1) {
      for (let i = 2; i < totalPages; i++) {
        if (i === currentPage - 1 || i === currentPage || i === currentPage + 1) {
          pageNumbers.push(i);
        } else if (i === currentPage - 2 && i > 1) {
          pageNumbers.push('...');
        } else if (i === currentPage + 2 && i < totalPages) {
          pageNumbers.push('...');
        }
      }
      if (!pageNumbers.includes(totalPages)) {
        if (pageNumbers[pageNumbers.length - 1] !== '...') {
          if (totalPages - pageNumbers[pageNumbers.length - 1] > 1) {
            pageNumbers.push('...');
          }
        }
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers.map((num, idx) => {
      if (num === '...') {
        return <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: theme.colors.text.disabled }}>...</span>;
      }
      return (
        <PageBtn
          key={num}
          $active={currentPage === num}
          onClick={() => setCurrentPage(num)}
          aria-label={`Page ${num}`}
        >
          {num}
        </PageBtn>
      );
    });
  };

  return (
    <ContentPanel>
      <TxToolbar>
        <TxSearchBox>
          <Search size={13} color="currentColor" />
          <input placeholder="Search transactions..." value={search} onChange={e => setSearch(e.target.value)} />
        </TxSearchBox>
        <ExportGroup>
          <ExportLabel>Export:</ExportLabel>
          <ExportBtn $csv onClick={handleExcel}><Download size={12} color="currentColor" /> Excel</ExportBtn>
          <ExportBtn onClick={handlePDF}><FileText size={12} color="currentColor" /> PDF</ExportBtn>
        </ExportGroup>
      </TxToolbar>
      <TxHead>
        <TxHeadCell>Date</TxHeadCell><TxHeadCell>Description</TxHeadCell>
        <TxHeadCell>Amount</TxHeadCell>
      </TxHead>
      {filtered.length === 0
        ? <div style={{ textAlign: 'center', padding: '24px', color: theme.colors.text.disabled, fontSize: 13 }}>No transactions found.</div>
        : paginatedData.map((tx, i) => (
          <TxRow key={i}>
            <TxCell color="currentColor">{tx.date}</TxCell>
            <TxCell>{tx.desc}</TxCell>
            <TxCell color={tx.isDebit ? theme.colors.danger.main : theme.colors.secondary.main} bold>{tx.amount}</TxCell>
          </TxRow>
        ))
      }

      {filtered.length > 0 && (
        <PaginationContainer>
          <PageControls>
            <PageBtn
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              aria-label="Previous page"
            >
              Prev
            </PageBtn>
            {renderPaginationNumbers()}
            <PageBtn
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              aria-label="Next page"
            >
              Next
            </PageBtn>
          </PageControls>
        </PaginationContainer>
      )}
    </ContentPanel>
  );
};

const StatementsTab = () => {
  const [selectedMonth, setSelectedMonth] = React.useState(STMT_MONTHS[0]);

  const handleDownload = (month) => {
    const content = `NYBANQ Statement\nPeriod: ${month}\n\nOpening Balance: $500,000.00\nClosing Balance: $750,000.00`;
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([content], { type: 'text/plain' })),
      download: `nybanq_statement_${month.replace(/ /g, '_')}.pdf`,
    });
    a.click();
  };

  return (
    <ContentPanel>
      <StmtRow>
        <StmtSelect value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
          {STMT_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
        </StmtSelect>
        <StmtDownloadBtn onClick={() => handleDownload(selectedMonth)}>
          <Download size={14} /> Download Statement
        </StmtDownloadBtn>
      </StmtRow>
      <StmtList>
        {STMT_MONTHS.map(month => (
          <StmtItem key={month}>
            <div>
              <StmtItemTitle>{month} Statement</StmtItemTitle>
              <StmtItemSub>PDF · Generated automatically</StmtItemSub>
            </div>
            <StmtDlBtn onClick={() => handleDownload(month)}><Download size={12} /> PDF</StmtDlBtn>
          </StmtItem>
        ))}
      </StmtList>
    </ContentPanel>
  );
};

const MobileDetailsWrapper = styled.div`
  @media (min-width: 1025px) {
    display: none;
  }
`;

const DesktopTabButton = styled.button`
  @media (max-width: 1024px) {
    display: none !important;
  }
`;

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

const MyAccountScreen = ({ onLogout, mode = 'financial' }) => {
  const location = useLocation();
  const accounts = MANAGER_ACCOUNTS[mode];

  const [selectedAccount, setSelectedAccount] = React.useState(() => {
    const id = location.state?.selectedAccountId;
    return id ? accounts.find(a => a.id === id) || accounts[0] : accounts[0];
  });

  const tabFromState = location.state?.myAccountTab;
  const isMobile = window.innerWidth <= 1024;
  const initialTab = tabFromState || (isMobile ? 'txHistory' : 'details');
  const [activeTab, setActiveTab] = React.useState(initialTab);
  const [showFedwireModal, setShowFedwireModal] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024 && activeTab === 'details') {
        setActiveTab('txHistory');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab]);

  React.useEffect(() => {
    if (location.state?.myAccountTab) setActiveTab(location.state.myAccountTab);
    if (location.state?.selectedAccountId) {
      const acc = accounts.find(a => a.id === location.state.selectedAccountId);
      if (acc) setSelectedAccount(acc);
    }
  }, [location.state, accounts]);

  const handleAccountChange = (e) => {
    const acc = accounts.find(a => a.id === parseInt(e.target.value));
    if (acc) setSelectedAccount(acc);
  };

  return (
    <PageTemplate title="My Account" onLogout={onLogout}>
      {/* Account Selector */}
      <SelectorContainer style={{ marginBottom: isMobile ? '0' : '20px' }}>
        <SelectorLabel>Select Account</SelectorLabel>
        <DropdownWrapper>
          <StyledSelect value={selectedAccount.id} onChange={handleAccountChange}>
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.currency} | {acc.name} ({acc.accountNumber})
              </option>
            ))}
          </StyledSelect>
          <DropdownIcon size={18} />
        </DropdownWrapper>
      </SelectorContainer>

      {/* Mobile Account Details (Always visible on mobile) */}
      <MobileDetailsWrapper>
        <AccountDetailsTab account={selectedAccount} onShowInstructions={() => setShowFedwireModal(true)} />
      </MobileDetailsWrapper>

      {/* Tabs (original AccountManager CSS design) */}
      <div className="tab-container" style={{ marginTop: isMobile ? '20px' : '0' }}>
        <DesktopTabButton onClick={() => setActiveTab('details')} className={`tab ${activeTab === 'details' ? 'active-tab' : ''}`}>Account Details</DesktopTabButton>
        <button onClick={() => setActiveTab('txHistory')} className={`tab ${activeTab === 'txHistory' ? 'active-tab' : ''}`}>TRANSACTION</button>
        <button onClick={() => setActiveTab('statements')} className={`tab ${activeTab === 'statements' ? 'active-tab' : ''}`}>STATEMENTS</button>
      </div>

      {activeTab === 'details' && window.innerWidth > 1024 && (
        <AccountDetailsTab account={selectedAccount} onShowInstructions={() => setShowFedwireModal(true)} />
      )}
      {activeTab === 'txHistory' && <TransactionHistoryTab />}
      {activeTab === 'statements' && <StatementsTab />}

      <FedwireInstructionsModal 
        show={showFedwireModal} 
        onClose={() => setShowFedwireModal(false)} 
        account={{ accountNumber: selectedAccount.accountNumber }}
      />
    </PageTemplate>
  );
};

export default MyAccountScreen;
